import { useEffect, useMemo } from 'react';

import WebApp from '@twa-dev/sdk';

import { isTMA } from '@/shared/helpers/is-tma';
import { useAppActions, useAppState } from '@/shared/store/app-store/app-store';
import { PageWrapperStyle, ThemeMode, UIKit } from '@/shared/store/app-store/types';
import { type HexColor } from '@/shared/types/common';
import {
  COLOR_PALETTES_CONFIG,
  ColorPalette,
  CUSTOM_PALETTE_STORAGE_KEY,
} from '@/shared/ui/constants';

type ValidTgColor = 'bg_color' | 'secondary_bg_color' | HexColor;

type PageBackground = {
  header: (
    isFullscreen: boolean
  ) => boolean | [ValidTgColor, ValidTgColor] | ValidTgColor;
  telegramBackground: ValidTgColor;
  background: [string, string] | string;
};

const DEFAULT_PAGE_BACKGROUND_BY_STYLE: Record<
  PageWrapperStyle,
  Record<UIKit, PageBackground>
> = {
  [PageWrapperStyle.COLORED_PRIMARY]: {
    [UIKit.APPLE]: {
      header: (isFullscreen) => (isFullscreen ? '#000000' : true),
      telegramBackground: 'bg_color',
      background: ['#ffffff', '#000000'],
    },
    [UIKit.MATERIAL]: {
      header: (isFullscreen) => (isFullscreen ? '#1e1e1e' : true),
      telegramBackground: 'bg_color',
      background: ['#ffffff', '#1e1e1e'],
    },
  },
  [PageWrapperStyle.COLORED_SECONDARY]: {
    [UIKit.APPLE]: {
      header: (isFullscreen) => (isFullscreen ? '#000000' : true),
      telegramBackground: 'secondary_bg_color',
      background: ['#efeff4', '#111111'],
    },
    [UIKit.MATERIAL]: {
      header: (isFullscreen) => (isFullscreen ? '#1e1e1e' : true),
      telegramBackground: 'secondary_bg_color',
      background: ['#f0f0f0', '#111111'],
    },
  },
  [PageWrapperStyle.PRIMARY]: {
    [UIKit.APPLE]: {
      header: (isFullscreen) => (isFullscreen ? ['#ffffff', '#1e1e1e'] : true),
      telegramBackground: 'bg_color',
      background: ['#ffffff', '#000000'],
    },
    [UIKit.MATERIAL]: {
      header: (isFullscreen) => (isFullscreen ? ['#ffffff', '#1e1e1e'] : true),
      telegramBackground: 'bg_color',
      background: ['#ffffff', '#1e1e1e'],
    },
  },
  [PageWrapperStyle.SECONDARY]: {
    [UIKit.APPLE]: {
      header: (isFullscreen) => (isFullscreen ? ['#ffffff', '#000000'] : true),
      telegramBackground: 'secondary_bg_color',
      background: ['#efeff4', '#111111'],
    },
    [UIKit.MATERIAL]: {
      header: (isFullscreen) => (isFullscreen ? ['#ffffff', '#000000'] : true),
      telegramBackground: 'secondary_bg_color',
      background: ['#f0f0f0', '#111111'],
    },
  },
};

const isValidTgColor = (color: string): color is ValidTgColor => {
  return (
    ['bg_color', 'secondary_color', 'secondary_bg_color'].includes(color) ||
    !!color.match(/^#([A-Fa-f0-9]{6})$/gm)?.[0]
  );
};

const getStyle = (pageWrapperStyle: PageWrapperStyle) => {
  return (
    DEFAULT_PAGE_BACKGROUND_BY_STYLE[pageWrapperStyle] ??
    DEFAULT_PAGE_BACKGROUND_BY_STYLE[PageWrapperStyle.PRIMARY]
  );
};

export const usePageWrapperStyle = () => {
  const { theme, uiKit, pageWrapperStyle } = useAppState();
  const { setTheme } = useAppActions();

  const isLightTheme = theme === ThemeMode.LIGHT;

  const {
    header: getHeader,
    background,
    telegramBackground,
  } = getStyle(pageWrapperStyle)[uiKit] ?? getStyle(pageWrapperStyle)[UIKit.MATERIAL];

  const header = getHeader(WebApp.isFullscreen);

  const pageBackground = useMemo(() => {
    let defaultBackground = background;

    if (Array.isArray(defaultBackground)) {
      defaultBackground = isLightTheme ? defaultBackground[0] : defaultBackground[1];
    }

    let res = defaultBackground;

    switch (pageWrapperStyle) {
      case PageWrapperStyle.PRIMARY:
        res = WebApp?.themeParams?.bg_color || defaultBackground;
        break;
      case PageWrapperStyle.SECONDARY:
        res = WebApp?.themeParams?.secondary_bg_color || defaultBackground;
        break;
      default:
        break;
    }

    return res;
  }, [background, isLightTheme, pageWrapperStyle]);

  useEffect(() => {
    if (header) {
      let headerColor: ValidTgColor | null = null;

      if (typeof header !== 'boolean') {
        if (Array.isArray(header)) {
          headerColor = isLightTheme ? header[0] : header[1];
        } else {
          headerColor = header;
        }
      } else {
        headerColor = telegramBackground;
      }

      if (headerColor && isValidTgColor(headerColor)) {
        if (isTMA()) WebApp.setHeaderColor(headerColor);
      }
    }

    if (isTMA()) WebApp.setBackgroundColor(telegramBackground);

    document.documentElement.style.setProperty('--page-background', pageBackground);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageBackground, pageWrapperStyle, telegramBackground, WebApp.isFullscreen]);

  useEffect(() => {
    const value = localStorage.getItem(CUSTOM_PALETTE_STORAGE_KEY);

    if (!value) return;

    const customPalette = Object.values(ColorPalette).includes(value)
      ? (value as ColorPalette)
      : null;

    if (!customPalette) return;

    const config = COLOR_PALETTES_CONFIG[customPalette];

    if (config.theme !== theme) {
      setTheme(COLOR_PALETTES_CONFIG[customPalette].theme);
    }

    for (const key in config) {
      if (key !== 'theme' && Object.prototype.hasOwnProperty.call(config, key)) {
        const color = config[key as keyof typeof config];

        document.documentElement.style.setProperty(key, color);
      }
    }
  }, [setTheme, theme]);

  return pageBackground;
};
